import React, { Component } from 'react'
import { connect } from 'react-redux'
import { SubmissionError, change } from 'redux-form'

import { disableOtp, generateOtp, updateAccount } from 'utils/io'
import { GOOGLE_AUTH_PROVIDER } from 'config'

import {
  getCurrentUser,
  getCurrentUserAuthProvider,
} from 'ducks/users/index.ts'

import Loading from 'components/Shared/Loading'
import EmptyState from 'components/Shared/EmptyState'
import Button from 'components/Shared/Button'
import {
  getCurrentOrganization,
  setCurrentOrganization,
  updateOrganizationName,
} from '../../../ducks/organizations'

import AccountForm, { FORM_NAME } from './AccountForm'
import TeamNameForm from './TeamNameForm'
import TwoFactorAuthentication from './TwoFactorAuthentication'
import { getFeatureFlag } from '../../../ducks/featureFlags'

class Account extends Component {
  state = {
    successBanner: false,
    error: null,
    passwordVisible: false,
    formVisible: false,
    teamFormVisible: false,
    formDirty: false,
  }

  togglePassword = event => {
    event.preventDefault()
    this.setState({ passwordVisible: true })
  }

  toggleForm = event => {
    event.preventDefault()
    this.setState({ formVisible: true })
  }

  toggleTeamNameForm = event => {
    event.preventDefault()

    this.setState({ teamFormVisible: true })
  }

  dirtyForm = dirty => {
    this.setState({ formDirty: dirty })
  }

  closeForm = () => {
    this.setState({ passwordVisible: false, formVisible: false })
  }

  closeTeamNameForm = () => {
    this.setState({ teamFormVisible: false })
  }

  handleSubmit = async accountData => {
    const { change } = this.props

    try {
      await updateAccount(accountData)

      window.setTimeout(() => this.setState({ successBanner: false }), 5000)

      this.setState({
        successBanner: true,
        error: null,
        passwordVisible: false,
        formVisible: false,
      })

      change(FORM_NAME, 'password', '')
      change(FORM_NAME, 'oldPassword', '')
    } catch (err) {
      if (err.errors) {
        this.setState({ successBanner: false, error: err.errors })
        throw new SubmissionError(err.errors)
      } else if (err.message) {
        this.setState({ successBanner: false, error: err.message })
        throw new SubmissionError({ _error: err.message })
      }
    }
  }

  handleChangeTeamName = async ({ name }) => {
    const { currentOrganization, updateOrganizationName } = this.props

    await updateOrganizationName(currentOrganization.id, name)

    this.closeTeamNameForm()
  }

  handleOTPGeneration = async () => {
    try {
      const data = await generateOtp()
      if (data.otpAuthUrl && data.otpSecret) {
        this.setState({
          otpAuthUrl: data.otpAuthUrl,
          otpSecret: data.otpSecret,
          showOtpModal: true,
        })
      }
    } catch (err) {
      this.setState({ success2FABanner: false, error2FA: err.message })
    }
  }

  handleOTPDisable = async authCode => {
    try {
      window.setTimeout(() => this.setState({ success2FABanner: false }), 5000)

      const disabled = await disableOtp(authCode)
      if (disabled) {
        this.setState({
          success2FABanner: '2 Factor Authentication Disabled!',
          error2FA: false,
        })

        this.toggleShowConfirmPassowordToDisable2FA()
      }
    } catch (err) {
      this.setState({ success2FABanner: false, error2FA: err.message })
    }
  }

  closeOtpModal = () => {
    this.setState({ showOtpModal: false })
  }

  handleOTPVerify = () => {
    window.setTimeout(() => this.setState({ success2FABanner: false }), 5000)

    this.setState({
      success2FABanner: '2 Factor Authentication Activated!',
    })
  }

  toggleShowConfirmPassowordToDisable2FA = () => {
    this.setState(prevState => ({
      showConfirmPassowordToDisable2FA:
        !prevState.showConfirmPassowordToDisable2FA,
    }))
  }

  render() {
    const {
      user,
      authProvider,
      currentOrganization,
      hasTwoFactorAuthentication,
    } = this.props

    const {
      successBanner,
      success2FABanner,
      error2FA,
      error,
      passwordVisible,
      formVisible,
      teamFormVisible,
      formDirty,
      otpAuthUrl,
      otpSecret,
      showOtpModal,
      showConfirmPassowordToDisable2FA,
    } = this.state

    if (!user) {
      return (
        <EmptyState greedy>
          <Loading />
        </EmptyState>
      )
    }

    const { email, name, developer } = user

    const initialValues = {
      email,
      name,
      developer,
    }

    return (
      <div className="account-settings">
        <>
          <h3 className="settings-header">Team</h3>
          <div className="settings-content-box">
            {!teamFormVisible && (
              <>
                <div className="settings-content-row">
                  <p className="settings-content-label">Team Name</p>
                  <h4>{currentOrganization?.name}</h4>
                </div>
              </>
            )}
            <TeamNameForm
              form={`teamName-${currentOrganization?.id}`}
              teamFormVisible={teamFormVisible}
              toggleTeamNameForm={this.toggleTeamNameForm}
              closeTeamNameForm={this.closeTeamNameForm}
              onSubmit={this.handleChangeTeamName}
              initialValues={{ name: currentOrganization?.name }}
              isAdmin={currentOrganization?.OrganizationUser?.isAdmin}
            />
          </div>
        </>

        <h3 className="settings-header">Profile</h3>
        {successBanner && (
          <div className="settings-form-alert settings-save-success">
            Successfully saved!
          </div>
        )}
        {error && typeof error === 'string' ? (
          <div className="settings-form-alert">{error}</div>
        ) : null}
        <div className="settings-content-box">
          {!formVisible && (
            <>
              <div className="settings-content-row">
                <p className="settings-content-label">Email Address</p>
                <h4>{initialValues?.email}</h4>
              </div>
              <div className="settings-content-row">
                <p className="settings-content-label">Your Name</p>
                <h4>{initialValues?.name}</h4>
              </div>
            </>
          )}
          <AccountForm
            onSubmit={this.handleSubmit}
            dirtyForm={this.dirtyForm}
            closeForm={this.closeForm}
            initialValues={initialValues}
            organization={currentOrganization}
            passwordVisible={passwordVisible}
            formVisible={formVisible}
          />
          {!passwordVisible && !formVisible && !formDirty && (
            <div className="setting-modal-button-row">
              <Button small outlined teal onClick={this.toggleForm}>
                Edit
              </Button>
              {authProvider !== GOOGLE_AUTH_PROVIDER && (
                <Button small outlined teal onClick={this.togglePassword}>
                  Reset Password
                </Button>
              )}
            </div>
          )}
        </div>
        {hasTwoFactorAuthentication && (
          <>
            <h3 className="settings-header">2 Factor Authentication</h3>
            {success2FABanner && (
              <div className="settings-form-alert settings-save-success">
                {success2FABanner}
              </div>
            )}
            {error2FA && typeof error2FA === 'string' ? (
              <div className="settings-form-alert">{error2FA}</div>
            ) : null}
            <TwoFactorAuthentication
              handleOTPSetup={this.handleOTPGeneration}
              handleOTPDisable={this.handleOTPDisable}
              handleOTPVerify={this.handleOTPVerify}
              otpAuthUrl={otpAuthUrl}
              otpSecret={otpSecret}
              showOtpModal={showOtpModal}
              closeModal={this.closeOtpModal}
              user={user}
              showConfirmPassowordToDisable2FA={
                showConfirmPassowordToDisable2FA
              }
              toggleShowConfirmPassowordToDisable2FA={
                this.toggleShowConfirmPassowordToDisable2FA
              }
            />
          </>
        )}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  user: getCurrentUser(state),
  currentOrganization: getCurrentOrganization(state),
  authProvider: getCurrentUserAuthProvider(state),
  hasTwoFactorAuthentication: getFeatureFlag(
    state,
    'hasTwoFactorAuthentication'
  ),
})

export default connect(mapStateToProps, {
  change,
  updateOrganizationName,
  setCurrentOrganization,
})(Account)
